import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Save, Loader2 } from 'lucide-react';
import { useRecordBulkStockCounts, useInventoryStock } from '@/hooks/useInventory';
import { CATEGORY_TYPES, CATEGORY_TYPE_COLORS, type CategoryType } from '@/constants/categoryTypes';
import { DateNavigator } from './DateNavigator';

interface ProductWithCategory {
  id: string;
  name: string;
  category_id: string | null;
  category_name: string | null;
  category_type: CategoryType | null;
}

interface StockCountEntry {
  physical_quantity: string;
  variance_reason: string;
}

interface BulkStockCountTableProps {
  onSaveSuccess?: () => void;
}

export function BulkStockCountTable({ onSaveSuccess }: BulkStockCountTableProps) {
  const [countDate, setCountDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState<string>('All');
  const [entries, setEntries] = useState<Record<string, StockCountEntry>>({});

  const recordBulkStockCounts = useRecordBulkStockCounts();
  const { data: stockData } = useInventoryStock();

  // Create a map of product_id to current stock quantity
  const stockMap = useMemo(() => {
    const map: Record<string, number> = {};
    stockData?.forEach((s) => {
      map[s.product_id] = s.current_quantity;
    });
    return map;
  }, [stockData]);

  // Fetch products with their categories
  const { data: products, isLoading } = useQuery({
    queryKey: ['products-with-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          category_id,
          category:categories(name, category_type)
        `)
        .order('name');

      if (error) throw error;

      return (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        category_id: p.category_id,
        category_name: p.category?.name || null,
        category_type: p.category?.category_type || null,
      })) as ProductWithCategory[];
    },
  });

  // Group products by category type
  const groupedProducts = useMemo(() => {
    if (!products) return { Fresh: [], FMCG: [], Other: [], All: [] };

    const grouped: Record<string, ProductWithCategory[]> = {
      Fresh: [],
      FMCG: [],
      Other: [],
      All: [...products],
    };

    products.forEach((product) => {
      const type = product.category_type || 'Other';
      if (grouped[type]) {
        grouped[type].push(product);
      } else {
        grouped.Other.push(product);
      }
    });

    // Sort All by category type then name
    grouped.All.sort((a, b) => {
      const typeOrder = { Fresh: 0, FMCG: 1, Other: 2 };
      const aOrder = typeOrder[a.category_type as keyof typeof typeOrder] ?? 2;
      const bOrder = typeOrder[b.category_type as keyof typeof typeOrder] ?? 2;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });

    return grouped;
  }, [products]);

  // Get products for current tab
  const displayProducts = groupedProducts[activeTab] || [];

  // Count entries with physical quantities entered
  const pendingCount = useMemo(() => {
    return Object.values(entries).filter(
      (e) => e.physical_quantity !== ''
    ).length;
  }, [entries]);

  // Calculate variance for a product
  const getVariance = (productId: string): number | null => {
    const entry = entries[productId];
    if (!entry || entry.physical_quantity === '') return null;
    const systemQty = stockMap[productId] || 0;
    return parseFloat(entry.physical_quantity) - systemQty;
  };

  // Update entry for a product
  const updateEntry = (productId: string, field: keyof StockCountEntry, value: string) => {
    setEntries((prev) => ({
      ...prev,
      [productId]: {
        physical_quantity: prev[productId]?.physical_quantity || '',
        variance_reason: prev[productId]?.variance_reason || '',
        [field]: value,
      },
    }));
  };

  // Handle bulk save
  const handleSave = async () => {
    const countsToRecord = Object.entries(entries)
      .filter(([, entry]) => entry.physical_quantity !== '')
      .map(([productId, entry]) => ({
        product_id: productId,
        count_date: countDate,
        system_quantity: stockMap[productId] || 0,
        physical_quantity: parseFloat(entry.physical_quantity),
        variance_reason: entry.variance_reason || undefined,
      }));

    if (countsToRecord.length === 0) return;

    await recordBulkStockCounts.mutateAsync(countsToRecord);

    // Clear entries after successful save
    setEntries({});
    onSaveSuccess?.();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with date and save button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="space-y-1">
            <Label>Count Date</Label>
            <DateNavigator date={countDate} onChange={setCountDate} />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={pendingCount === 0 || recordBulkStockCounts.isPending}
          className="gap-2"
        >
          {recordBulkStockCounts.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {pendingCount > 0 ? `Save ${pendingCount} Counts` : 'Save Counts'}
        </Button>
      </div>

      {/* Category tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {CATEGORY_TYPES.map((type) => (
            <TabsTrigger key={type} value={type} className="gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  type === 'Fresh'
                    ? 'bg-green-500'
                    : type === 'FMCG'
                    ? 'bg-blue-500'
                    : 'bg-gray-500'
                }`}
              />
              {type}
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {groupedProducts[type]?.length || 0}
              </Badge>
            </TabsTrigger>
          ))}
          <TabsTrigger value="All" className="gap-2">
            All
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {products?.length || 0}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Table content for each tab */}
        {[...CATEGORY_TYPES, 'All'].map((tabValue) => (
          <TabsContent key={tabValue} value={tabValue} className="mt-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Product</TableHead>
                    {tabValue === 'All' && <TableHead className="w-[100px]">Category</TableHead>}
                    <TableHead className="w-[120px] text-right">System Qty</TableHead>
                    <TableHead className="w-[120px]">Physical Count</TableHead>
                    <TableHead className="w-[100px] text-right">Variance</TableHead>
                    <TableHead>Reason (if variance)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayProducts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={tabValue === 'All' ? 6 : 5}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No products in this category
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayProducts.map((product) => {
                      const systemQty = stockMap[product.id] || 0;
                      const variance = getVariance(product.id);
                      const hasVariance = variance !== null && variance !== 0;

                      return (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          {tabValue === 'All' && (
                            <TableCell>
                              {product.category_type && (
                                <Badge
                                  variant="outline"
                                  className={CATEGORY_TYPE_COLORS[product.category_type as CategoryType]}
                                >
                                  {product.category_type}
                                </Badge>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-right font-mono text-muted-foreground">
                            {systemQty.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Count"
                              value={entries[product.id]?.physical_quantity || ''}
                              onChange={(e) => updateEntry(product.id, 'physical_quantity', e.target.value)}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            {variance !== null && (
                              <Badge
                                variant={variance > 0 ? 'default' : variance < 0 ? 'destructive' : 'secondary'}
                              >
                                {variance > 0 ? '+' : ''}{variance.toLocaleString()}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder={hasVariance ? 'Explain variance...' : 'Optional'}
                              value={entries[product.id]?.variance_reason || ''}
                              onChange={(e) => updateEntry(product.id, 'variance_reason', e.target.value)}
                              className="w-full"
                              disabled={!hasVariance}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Pending save indicator */}
      {pendingCount > 0 && (
        <div className="text-sm text-muted-foreground">
          {pendingCount} product{pendingCount !== 1 ? 's' : ''} with counts will be saved
        </div>
      )}
    </div>
  );
}
